import { OnChangePlugin as LexicalOnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import React, { useEffect, useRef } from "react";
import { useAnnoContextLex } from "../AnnoPlugin/context";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNodeByKey } from "lexical";
import { $convertToMarkdownString, TRANSFORMERS } from "@lexical/markdown";

export const OnChangePlugin = ({ id, parentOnChange, saveMarkdown }) => {
  const { annoData, latestAnnoDataRef, isAnnoMutatingRef } = useAnnoContextLex();
  const [editor] = useLexicalComposerContext();
  const isFirstMount = useRef(true);

  /**
   * filter out the unused annodata, since unused anno data is only
   * needed by the internal undo-redo logic, which the parent comp
   * doesnt have to care about
   */
  const filterOutUnusedAnnoData = (annoData) => {
    let annoDataToSend;
    if (annoData) {
      annoDataToSend = Object.keys(annoData).reduce((a, b) => {
        if (annoData[b].IS_UNUSED) {
          return a;
        } else {
          return {
            ...a,
            [b]: annoData[b],
          };
        }
      }, {});
    }
    return annoDataToSend;
  };

  const getAllText = () => {
    let allText = "";
    editor.getEditorState().read(() => {
      const rootNode = $getNodeByKey("root");
      allText = rootNode.getTextContent();
    });
    return allText;
  };

  const getMarkdownText = () => {
    if (!saveMarkdown) return undefined;
    let markdownText = "";
    editor.getEditorState().read(() => {
      markdownText = $convertToMarkdownString(TRANSFORMERS);
    });
    return markdownText;
  };

  function onEditorStateChange(editorState) {
    // Skip while an annotation mutation is in flight — Path 2 (the annoData
    // useEffect below) will fire once after React renders with the final
    // clean state. See isAnnoMutatingRef declaration in AnnoPlugin/context.js.
    if (isAnnoMutatingRef?.current) {
      // eslint-disable-next-line no-console
      console.log("[OnChangePlugin] Path 1 (LexicalOnChange) → SKIP (anno mutation in flight)");
      return;
    }

    // Read latest annoData via the ref rather than closure. LexicalOnChangePlugin
    // fires synchronously inside editor.update's commit — at that moment React
    // hasn't re-rendered yet, so the closure `annoData` is one frame stale.
    const latestAnnoData = latestAnnoDataRef?.current ?? annoData;

    const valuePayload = {
      allText: getAllText(),
      isLexical: true, //useful for diffenciating between draftjs & lexical, since both will have to exist in OKF at the sametime
      editorState: editorState.toJSON(),
      annoData: filterOutUnusedAnnoData(latestAnnoData),
    };

    // Only add markdownText if saveMarkdown is true
    if (saveMarkdown) {
      valuePayload.markdownText = getMarkdownText();
    }

    // eslint-disable-next-line no-console
    console.log("[OnChangePlugin] Path 1 (LexicalOnChange) → FIRE");
    parentOnChange && parentOnChange(id, valuePayload);
  }

  /**
   * the way lexical is designed, it would be quite an anti pattern to try and
   * store anno data within the editorstate. Hence the 'shape' of our LexicalTextEditor
   * value will be a bit different from the rest of our tplBlocks. It will have to
   * be: { editorState: <EditorState>, annoData: <AnnoData> }
   *
   */
  useEffect(() => {
    if (!isFirstMount.current) {
      if (parentOnChange) {
        const payload = {
          allText: getAllText(),
          isLexical: true, //useful for diffenciating between draftjs & lexical, since both will have to exist in OKF at the sametime
          editorState: editor.getEditorState().toJSON(),
          annoData: filterOutUnusedAnnoData(annoData),
        };

        // Only add markdownText if saveMarkdown is true
        if (saveMarkdown) {
          payload.markdownText = getMarkdownText();
        }

        // eslint-disable-next-line no-console
        console.log("[OnChangePlugin] Path 2 (annoData useEffect) → FIRE");
        parentOnChange(id, payload);
      }
    } else {
      isFirstMount.current = false;
    }
  }, [JSON.stringify(annoData), editor, saveMarkdown]); // Added saveMarkdown to dependency array

  // v.imp annoData is strigified else we go into endless loop.
  // its not very optimum if annoData becomes big, but we dont have
  // a better solution for now

  return (
    <LexicalOnChangePlugin
      ignoreSelectionChange={true}
      onChange={onEditorStateChange}
    />
  );
};
